class VariantSelects extends HTMLElement {
    constructor() {
        super();
        this.item = $(this).closest('.productView');
        this.isFullWidth = this.item.is('.layout-4');

        this.addEventListener('change', this.onVariantChange.bind(this));
        this.onRubikVariantChange = (event) => {
            const productId = event.detail?.productId || event.detail?.product_id;
            if (productId && String(productId) !== this.dataset.product) return;
            this.onVariantChange(event);
        };
        this.onRubikReady = () => this.syncRubikOptions();
        this.onVariantInit();
    }

    connectedCallback() {
        document.addEventListener('rubik-variant-change', this.onRubikVariantChange);
        document.addEventListener('rubik-swatch-ready', this.onRubikReady);
        document.addEventListener('rubik-variant-id-changed', this.onRubikReady);
        this.syncRubikOptions();
        this.syncLinkedPickers();
        this.updateMedia(0);
        if (this.item[0]?.querySelector('.productView-nav[data-image-sizes-full]')) {
            this.galleryImagesObserver = new MutationObserver(() => this.syncGalleryImageSizes());
            this.galleryImagesObserver.observe(this.item[0], { childList: true, subtree: true });
            this.syncGalleryImageSizes();
        }
    }

    disconnectedCallback() {
        this.variantRequestVersion += 1;
        this.variantRequestController?.abort();
        this.galleryImagesObserver?.disconnect();
        document.removeEventListener('rubik-variant-change', this.onRubikVariantChange);
        document.removeEventListener('rubik-swatch-ready', this.onRubikReady);
        document.removeEventListener('rubik-variant-id-changed', this.onRubikReady);
        clearTimeout(this.rubikSizeSyncTimer);
        this.item[0]?.removeEventListener('submit', this.variantSubmitGuard, true);
        this.variantSubmitGuard = null;
    }

    onVariantInit() {
        this.variantRequestVersion = 0;
        this.updateOptions();
        const defaultSizeChanged = this.selectInitialSize();
        this.updateMasterId();
        this.updateVariantStatuses();
        this.updateVariantInput();
        if (!this.currentVariant) {
            this.updateAttribute(true);
            this.updateStickyAddToCart(true);
            this.setVariantState('unavailable', 'Esta combinação não está disponível. Escolha outra opção.');
            return;
        }
        this.renderProductInfo();
        this.updateAttribute(false, !this.currentVariant.available);
        this.updateStickyAddToCart(false, !this.currentVariant.available);
        if (!this.closest('.featured-product')) this.updateURL();
        this.setVariantState('ready', this.getSoldOutGuidance()?.message);
        if (defaultSizeChanged) {
            this.setVariantState('loading', 'Atualizando preço e disponibilidade...');
            this.renderProductAjaxInfo();
        }
    }

    selectInitialSize() {
        const params = new URLSearchParams(window.location.search);
        if (params.has('variant') || params.has('option_values') || this.closest('.featured-product')) return false;
        return this.selectSmallestAvailableSize();
    }

    selectSmallestAvailableSize() {
        if (!this.hasAttribute('data-size-option-index')) return false;
        const sizeIndex = Number(this.dataset.sizeOptionIndex);
        const wrapper = this.querySelectorAll('.product-form__input')[sizeIndex];
        if (!wrapper) return false;
        const options = this.options.map(value => this.decodeOption(value));
        const candidates = this.getVariantData().filter(variant => variant.available &&
            options.every((value, index) => index === sizeIndex || value === variant.options[index]));
        const standardSizes = ['PP', 'P', 'M', 'G', 'GG', 'XG', 'XGG'];
        const sizes = [...new Set(candidates.map(variant => variant.options[sizeIndex]))].sort((first, second) => {
            const firstRank = standardSizes.indexOf(first.toUpperCase());
            const secondRank = standardSizes.indexOf(second.toUpperCase());
            if (firstRank >= 0 && secondRank >= 0) return firstRank - secondRank;
            return first.localeCompare(second, 'pt-BR', { numeric: true });
        });
        const size = sizes[0];
        if (!size || size === options[sizeIndex]) return false;
        const select = wrapper.querySelector('select');
        if (select) {
            const option = [...select.options].find(option => this.decodeOption(option.value) === size);
            if (!option) return false;
            select.value = option.value;
        } else {
            const radio = [...wrapper.querySelectorAll('input[type=radio]')].find(input => this.decodeOption(input.value) === size);
            if (!radio) return false;
            radio.checked = true;
        }
        this.updateOptions();
        return true;
    }

    syncRubikOptions() {
        if (!this.currentVariant) return;
        clearTimeout(this.rubikSizeSyncTimer);
        this.rubikSizeSyncTimer = setTimeout(() => {
            if (!this.isConnected) return;
            const swatch = this.item[0]?.querySelector(`rubik-swatch[product-id="${this.dataset.product}"]`);
            let changedInput;
            this.currentVariant.options.forEach((value, index) => {
                const input = [...(swatch?.shadowRoot?.querySelectorAll('input[data-rubik-option-position]') || [])]
                    .find(input => Number(input.dataset.rubikOptionPosition) === index + 1 && input.value === value);
                if (!input || input.checked || input.disabled) return;
                input.checked = true;
                changedInput ||= input;
            });
            changedInput?.dispatchEvent(new Event('change', { bubbles: true, composed: true }));
        }, 0);
    }

    setSelectedOptions(options) {
        const wrappers = Array.from(this.querySelectorAll('.product-form__input'));
        const inputs = options.map((value, index) => {
            const wrapper = wrappers[index];
            const select = wrapper?.querySelector('select');
            return [...(select?.options || wrapper?.querySelectorAll('input[type="radio"]') || [])]
                .find(input => this.decodeOption(input.value) === value);
        });
        if (inputs.some(input => !input)) return false;
        const changedInput = inputs.find(input => input.tagName === 'OPTION'
            ? input.parentElement.value !== input.value : !input.checked);
        const requestVersion = this.variantRequestVersion;
        inputs.forEach((input) => {
            if (input.tagName === 'OPTION') input.parentElement.value = input.value;
            else input.checked = true;
        });
        if (changedInput) {
            const target = changedInput.tagName === 'OPTION' ? changedInput.parentElement : changedInput;
            target.dispatchEvent(new Event('change', { bubbles: true }));
        }
        const stateChanged = changedInput || this.options.some((value, index) => this.decodeOption(value) !== options[index]) ||
            !this.currentVariant?.options.every((value, index) => value === options[index]);
        if (stateChanged && this.variantRequestVersion === requestVersion) this.onVariantChange();
        this.syncRubikOptions();
        return true;
    }

    syncLinkedPickers() {
        const pickers = this.item[0]?.querySelectorAll('variant-sticky-radios, variant-sticky-selects');
        pickers?.forEach((picker) => {
            if (picker.dataset.product !== this.dataset.product || picker.dataset.section !== this.dataset.section) return;
            picker.options = [...this.options];
            picker.currentVariant = this.currentVariant;
            if (picker.matches('variant-sticky-selects')) {
                const select = picker.querySelector('select');
                if (select) select.value = this.currentVariant?.id || '';
                return;
            }
            picker.querySelectorAll('.product-form__input').forEach((wrapper, index) => {
                const value = this.options[index];
                const header = wrapper.querySelector('[data-header-option]');
                if (header) header.textContent = value || '';
                const candidates = this.getVariantData().filter((variant) =>
                    this.options.slice(0, index).every((selected, previous) => selected === variant.options[previous]));
                wrapper.querySelectorAll('input[type="radio"]').forEach((input) => {
                    const optionValue = this.decodeOption(input.value);
                    input.checked = optionValue === value;
                    const label = input.labels?.[0] || input.nextElementSibling;
                    if (!label) return;
                    const exists = candidates.some(variant => variant.options[index] === optionValue);
                    const available = candidates.some(variant => variant.available && variant.options[index] === optionValue);
                    label.classList.toggle('available', available);
                    label.classList.toggle('soldout', !available);
                    label.classList.toggle('unavailable', !input.checked && window.variantStrings.hide_variants_unavailable && !exists);
                });
            });
        });
    }

    onVariantChange() {
        const previousOptions = this.options?.map(value => this.decodeOption(value)) || [];
        this.variantRequestVersion += 1;
        this.variantRequestController?.abort();
        this.updateOptions();
        this.updateMasterId();
        if (this.hasAttribute('data-size-option-index')) {
            const sizeIndex = Number(this.dataset.sizeOptionIndex);
            const otherOptionChanged = this.options.some((value, index) => index !== sizeIndex && value !== previousOptions[index]);
            if (otherOptionChanged && !this.currentVariant?.available) {
                this.selectSmallestAvailableSize();
                this.updateMasterId();
                this.syncRubikOptions();
            }
        }
        this.updateVariantStatuses();
        this.updateVariantInput();
        this.updatePickupAvailability();

        if (!this.currentVariant) {
            this.updateAttribute(true);
            this.updateStickyAddToCart(true);
            this.setVariantState('unavailable', 'Esta combinação não está disponível. Escolha outra opção.');
            return;
        }

        this.updateMedia(200);
        if (!this.closest('.featured-product')) this.updateURL();
        this.renderProductInfo();
        this.checkQuantityWhenVariantChange();
        this.updateStickyAddToCart(false, !this.currentVariant.available);
        this.setVariantState('loading', 'Atualizando preço e disponibilidade...');
        this.renderProductAjaxInfo();
        const productFancybox = this.item.find('[data-fancybox]');
        if (productFancybox.length && typeof $.fn.fancybox === 'function') {
            productFancybox.fancybox({
                buttons: ['zoom', 'share', 'slideShow', 'fullScreen', 'thumbs', 'close'],
                loop: true,
                thumbs: { autoStart: true }
            });
        }
    }

    setVariantState(state, message = '') {
        this.dataset.variantState = state;
        const root = this.item[0];
        const blocked = state !== 'ready';
        this.getProductForms().forEach((form) => {
            form.dataset.variantState = state;
            form.setAttribute('aria-busy', String(state === 'loading'));
            form.querySelectorAll('input[name="id"]').forEach((input) => { input.disabled = blocked; });
            form.querySelectorAll('[name="add"]').forEach((button) => {
                if (blocked || form.cartAddPromise) button.disabled = true;
            });
        });
        if (root && !this.variantSubmitGuard) {
            this.variantSubmitGuard = (event) => {
                if (this.dataset.variantState !== 'ready' && this.getProductForms().includes(event.target)) {
                    event.preventDefault();
                    event.stopImmediatePropagation();
                }
            };
            root.addEventListener('submit', this.variantSubmitGuard, true);
        }
        [document.getElementById(`product-price-${this.dataset.product}`),
            document.getElementById(`product-installments-${this.dataset.product}`),
            ...(root?.querySelectorAll('.productView-stickyCart .sticky-price') || [])].forEach((element) => {
            if (!element) return;
            element.classList.toggle('visibility-hidden', blocked);
            element.setAttribute('aria-hidden', String(blocked));
        });
        root?.querySelectorAll('.shopify-payment-button').forEach((element) => {
            if (blocked && !element.hasAttribute('data-variant-visibility')) {
                element.dataset.variantVisibility = element.style.visibility;
            }
            if (blocked) element.style.visibility = 'hidden';
            else if (element.hasAttribute('data-variant-visibility')) {
                element.style.visibility = element.dataset.variantVisibility;
                delete element.dataset.variantVisibility;
            }
        });
        if (!this.variantStatus) {
            this.variantStatus = document.createElement('div');
            this.variantStatus.className = 'product-variant-status';
            this.variantStatus.setAttribute('role', 'status');
            this.variantStatus.setAttribute('aria-live', 'polite');
            this.variantStatus.setAttribute('aria-atomic', 'true');
            this.insertAdjacentElement('afterend', this.variantStatus);
        }
        this.variantStatus.replaceChildren();
        this.variantStatus.hidden = !message;
        if (message) {
            const text = document.createElement('p');
            text.textContent = message;
            this.variantStatus.append(text);
        }
        if (state === 'error') {
            const retry = document.createElement('button');
            retry.type = 'button';
            retry.className = 'button button--secondary';
            retry.textContent = 'Tentar novamente';
            retry.addEventListener('click', () => this.onVariantChange());
            this.variantStatus.append(retry);
        }
    }

    getProductForms() {
        return Array.from(document.querySelectorAll(`#product-form-${this.dataset.product}, #product-form-installment-${this.dataset.product}, #product-form-sticky-${this.dataset.product}`));
    }

    updateOptions() {
        this.options = Array.from(this.querySelectorAll('select'), (select) => select.value);
    }

    decodeOptions() {
        this.options = this.options.map(option => {
            const parsedOption = this.decodeOption(option)
            return parsedOption
        })
    }
  
    decodeOption(option) {
      if (option) {
          return option.split('Special_Double_Quote').join('"').split('Special_Slash').join('/')
        } else {
          return null
        }
    }

    encodeOption(option) {
        if (option) {
          return option.split('"').join('Special_Double_Quote').split('/').join('Special_Slash')
        } else {
          return null
        }
    }
    
    updateMasterId() {
        this.decodeOptions()
        this.currentVariant = this.getVariantData().find((variant) => {
            return !variant.options.map((option, index) => {
                return this.options[index] === option;
            }).includes(false);
        });

        if (this.item.find('[data-filter]').length && this.currentVariant?.featured_media) {
            this.item.find('[data-filter]:checked').attr('data-value-default-lang', this.currentVariant?.option1.toLowerCase().replace(/ /g,"-"));
        }
    }      

    updateMedia(time) {
        if (!this.currentVariant || !this.currentVariant?.featured_media) return;
        const mediaId = String(this.currentVariant.featured_media.id);
        const gallery = this.item[0]?.querySelector('.productView-nav');
        if (!gallery) return;

        const syncMedia = (attempt = 0) => {
            if (!gallery.isConnected || String(this.currentVariant?.featured_media?.id) !== mediaId) return;

            const $gallery = $(gallery);
            if ($gallery.hasClass('slick-initialized')) {
                const slides = [...$gallery.slick('getSlick').$slides];
                const index = slides.findIndex((slide) => [...slide.querySelectorAll('[data-media-id]')]
                    .some((media) => media.dataset.mediaId === mediaId));
                if (index < 0) return;

                const thumbnails = this.item[0].querySelector('.productView-for.mobile');
                const $thumbnails = $(thumbnails);
                let thumbnailId;
                if (thumbnails && $thumbnails.hasClass('slick-initialized')) {
                    thumbnailId = `${this.dataset.section}-${mediaId}`;
                    const thumbnailSlides = [...$thumbnails.slick('getSlick').$slides];
                    const thumbnailIndex = thumbnailSlides.findIndex((slide) => slide.dataset.mediaId === thumbnailId);
                    if (thumbnailIndex >= 0) {
                        $thumbnails.slick('slickGoTo', thumbnailIndex, true);
                    }
                }
                $gallery.slick('slickGoTo', index, true);

                const activeMediaId = gallery.querySelector('.slick-current [data-media-id]')?.dataset.mediaId;
                const activeThumbnailId = thumbnails?.querySelector('.slick-current[data-media-id]')?.dataset.mediaId;
                if ((activeMediaId !== mediaId || (thumbnailId && activeThumbnailId !== thumbnailId)) && attempt < 8) {
                    window.setTimeout(() => syncMedia(attempt + 1), 100);
                    return;
                }

                if (activeMediaId === mediaId) {
                    const slick = $gallery.slick('getSlick');
                    const counter = gallery.querySelector(':scope > .slick-counter');
                    if (counter) {
                        counter.querySelector('.slick-counter--current').textContent = slick.currentSlide + 1;
                        counter.querySelector('.slick-counter--total').textContent = slick.slideCount;
                    }
                }
                return;
            }

            if (time === 0 || window.innerWidth < 768 ||
                !document.body.classList.contains('template-product') ||
                !this.item[0].closest('#MainContent') ||
                !gallery.classList.contains('style-3')) return;

            const selectedMedia = [...gallery.querySelectorAll('.productView-image [data-media-id]')]
                .find((media) => media.dataset.mediaId === mediaId)?.closest('.productView-image');
            const firstMedia = gallery.querySelector(':scope > .productView-image');
            if (!selectedMedia || !firstMedia) return;

            if (selectedMedia !== firstMedia) gallery.insertBefore(selectedMedia, firstMedia);
            this.syncGalleryImageSizes();
        };

        if (time > 0) {
            window.setTimeout(syncMedia, time);
        } else {
            syncMedia();
        }
        if (!$(gallery).hasClass('slick-initialized') &&
            (window.innerWidth < 768 || !gallery.matches('.style-2, .style-3'))) {
            $(gallery).one('init', () => window.requestAnimationFrame(syncMedia));
        }
    }

    syncGalleryImageSizes() {
        const gallery = this.item[0]?.querySelector('.productView-nav[data-image-sizes-full]');
        if (!gallery?.classList.contains('style-3') || gallery.classList.contains('slick-initialized')) return;

        [...gallery.children].filter((media) => media.classList.contains('productView-image'))
            .forEach((media, index) => {
                media.dataset.index = String(index + 1);
                const imageSizes = index % 3 === 2
                    ? gallery.dataset.imageSizesFull
                    : gallery.dataset.imageSizesHalf;
                media.querySelectorAll('[data-cursor-image][data-index]').forEach((image) => {
                    image.dataset.index = String(index + 1);
                    if (imageSizes && image.matches('img[data-main-image]')) {
                        const sizes = (image.loading === 'lazy' ? 'auto, ' : '') + imageSizes;
                        if (image.sizes !== sizes) image.sizes = sizes;
                    }
                });
            });
    }

    updateURL() {
        if (!this.currentVariant) return;
        const url = new URL(window.location.href);
        url.searchParams.set('variant', this.currentVariant.id);
        window.history.replaceState(window.history.state, '', url);
    }

    updateVariantInput() {
        this.getProductForms().forEach((productForm) => {
            const input = productForm.querySelector('input[name="id"]');
            if (!input) return;
            input.value = this.currentVariant?.id || '';
            input.disabled = !this.currentVariant;
            input.dispatchEvent(new Event('change', { bubbles: true }));
        });
    }

    updatePickupAvailability() {
        const pickUpAvailability = document.querySelector('pickup-availability');
        if (!pickUpAvailability) return;

        if (this.currentVariant?.available) {
            pickUpAvailability.fetchAvailability(this.currentVariant.id);
        } else {
            pickUpAvailability.removeAttribute('available');
            pickUpAvailability.innerHTML = '';
        }
    }

    async renderProductAjaxInfo() {
        const variant = this.currentVariant;
        if (!variant) return;
        const version = this.variantRequestVersion;
        const controller = new AbortController();
        this.variantRequestController = controller;
        const timeout = window.setTimeout(() => controller.abort(), 15000);
        const isCurrent = () => this.isConnected && version === this.variantRequestVersion && this.currentVariant?.id === variant.id;
        const url = new URL(this.dataset.url, window.location.origin);
        url.searchParams.set('variant', variant.id);
        url.searchParams.set('section_id', this.dataset.section);
        try {
            const response = await fetch(url, { signal: controller.signal });
            if (!response.ok) throw new Error('Variant section request failed');
            const responseText = await response.text();
            if (!isCurrent()) return;
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            const selectedInput = html.querySelector(`#product-form-${this.dataset.product} input[name="id"]`);
            if (!selectedInput || selectedInput.value !== String(variant.id)) throw new Error('Variant section is incomplete');
            const replacements = [`product-price-${this.dataset.product}`, `product-installments-${this.dataset.product}`].map((id) => ({
                destination: document.getElementById(id), source: html.getElementById(id)
            }));
            if (replacements.some(({ destination, source }) => destination && !source)) throw new Error('Variant price is incomplete');
            replacements.forEach(({ destination, source }) => {
                if (destination && source) {
                    destination.innerHTML = source.innerHTML;
                    destination.dataset.variantId = variant.id;
                }
            });
            const propertyId = `product-property-${this.dataset.product}`;
            const destinationProperty = document.getElementById(propertyId);
            const sourceProperty = html.getElementById(propertyId);
            if (destinationProperty) {
                destinationProperty.innerHTML = sourceProperty?.innerHTML || '';
                destinationProperty.style.display = sourceProperty ? 'table' : 'none';
            } else if (sourceProperty) {
                const options = this.item[0]?.querySelector('.productView-options');
                options?.insertAdjacentElement('beforebegin', sourceProperty);
            }
            this.updateProductInfo(html);
            if (this.checkNeedToConvertCurrency()) {
                const currencyCode = document.getElementById('currencies')?.querySelector('.active')?.getAttribute('data-currency');
                Currency.convertAll(window.shop_currency, currencyCode, 'span.money', 'money_format');
            }
            this.updateBadgeSale();
            this.updateAttribute(false, !variant.available);
            this.updateStickyAddToCart(false, !variant.available);
            this.setVariantState('ready', this.getSoldOutGuidance()?.message);
        } catch (error) {
            if (!isCurrent()) return;
            this.setVariantState('error', 'Não foi possível atualizar esta opção. Tente novamente ou escolha outra opção.');
        } finally {
            window.clearTimeout(timeout);
            if (isCurrent()) this.variantRequestController = null;
        }
    }

    renderProductInfo() {
        if (!this.currentVariant) return;
        if(this.item.find('[data-sku]').length > 0){
            this.item.find('[data-sku] .productView-info-value').text(this.currentVariant.sku);
        }

        if(this.item.find('[data-barcode]').length > 0){
            this.item.find('[data-barcode] .productView-info-value').text(this.currentVariant.barcode);
        }

        var inventory = this.currentVariant?.inventory_management;

        if(inventory != null) {
            var arrayInVarName = `product_inven_array_${this.dataset.product}`,
                inven_array = window[arrayInVarName];

            if(inven_array != undefined) {
                var inven_num = inven_array[this.currentVariant.id],
                    inputQuantity = this.item.find('input[name="quantity"]'),
                    buttonSubmit = this.item.find('button#product-add-to-cart'),
                    inventoryQuantity = parseInt(inven_num);

                if(inputQuantity.length > 0) {
                    inputQuantity.attr('data-inventory-quantity', inventoryQuantity);
                } else {
                    buttonSubmit.attr('data-inventory-quantity', inventoryQuantity);
                }

                if(this.item.find('[data-inventory]').length > 0){
                    if(inventoryQuantity > 0){
                        const showStock = this.item.find('[data-inventory]').data('stock-level');
                        if (showStock == 'show') {
                            this.item.find('[data-inventory] .productView-info-value').text(inventoryQuantity+' '+window.inventory_text.inStock);
                        }
                        else {
                            this.item.find('[data-inventory] .productView-info-value').text(window.inventory_text.inStock);
                        }
                    } else {
                        this.item.find('[data-inventory] .productView-info-value').text(window.inventory_text.outOfStock);
                    }
                }

                hotStock(inventoryQuantity);
            }
        }

        if(this.item.find('.productView-stickyCart').length > 0){
            const sticky = this.item.find('.productView-stickyCart');
            const optionSticky = sticky.find('.select__select');

            optionSticky.val(this.currentVariant.id);
        }
    }

    updateProductInfo(html) {
        const selector = `[data-product-description-${this.dataset.product}]`;
        const destination = this.item[0]?.querySelector(selector);
        const source = html.querySelector(selector);
        if (destination && source) {
            destination.innerHTML = source.innerHTML;
            if (destination.closest('.toggle-content--height')) destination.style.maxHeight = null;
        }
    }

    updateAttribute(unavailable = true, disable = true){
        const addButton = document.getElementById(`product-form-${this.dataset.product}`)?.querySelector('[name="add"]');
        if (!addButton) return;
        var quantityInput = this.item.find('input[name="quantity"]'),
            notifyMe = this.item.find('.productView-notifyMe'),
            hotStock = this.item.find('.productView-hotStock'),
            buttonAddtocart = this.item.find('.product-form__submit'),
            maxValue = parseInt(quantityInput.attr('data-inventory-quantity'));

        if (isNaN(maxValue)) {
            maxValue = maxValue = parseInt(buttonAddtocart.attr('data-inventory-quantity'));
        } else {
            maxValue = parseInt(quantityInput.attr('data-inventory-quantity'));
        }
        
        if(unavailable){
            var text = window.variantStrings.unavailable;

            quantityInput.attr('disabled', true);
            notifyMe.slideUp('slow');
            addButton.setAttribute('disabled', true);
            addButton.textContent = text;
            quantityInput.closest('quantity-input').addClass('disabled');

            if(hotStock.length > 0) hotStock.addClass('is-hiden');
        } else {
            if (disable) {
                var text = this.getSoldOutGuidance()?.button || window.variantStrings.soldOut,
                    subTotal = 0,
                    price = this.currentVariant?.price;

                const stickyPrice = $('[data-sticky-add-to-cart] .money-subtotal .money');
                const stickyComparePrice = $('[data-sticky-add-to-cart] .money-compare-price .money');

                if (window.subtotal.show) {
                    let qty = quantityInput.val() || 1;

                    subTotal = qty * price;
                    subTotal = Shopify.formatMoney(subTotal, window.money_format);
                    subTotal = extractContent(subTotal);

                    const moneySpan = document.createElement('span')
                    moneySpan.classList.add(window.currencyFormatted ? 'money' : 'money-subtotal')
                    moneySpan.innerText = subTotal
                    document.body.appendChild(moneySpan)

                    if (this.checkNeedToConvertCurrency()) {
                        let currencyCode = document.getElementById('currencies')?.querySelector('.active')?.getAttribute('data-currency');
                        Currency.convertAll(window.shop_currency, currencyCode, 'span.money', 'money_format');
                    }

                    subTotal = moneySpan.innerText
                    $(moneySpan).remove()

                    if (window.subtotal.style == '1') {
                        const pdView_subTotal = document.querySelector('.productView-subtotal .money') || document.querySelector('.productView-subtotal .money-subtotal');
                        if (pdView_subTotal != null) {
                            pdView_subTotal.textContent = subTotal;
                        }
                    } else if (window.subtotal.style == '2') {
                        text = window.subtotal.text.replace('[value]', subTotal);
                        console.log(text,'123');
                    }
                } else {
                    subTotal = Shopify.formatMoney(price, window.money_format);
                    subTotal = extractContent(subTotal);
                }

                quantityInput.attr('data-price', this.currentVariant?.price);
                quantityInput.attr('disabled', true);
                addButton.setAttribute('disabled', true);
                addButton.textContent = text;
                quantityInput.closest('quantity-input').addClass('disabled');

                if (subTotal != 0 && stickyPrice.length) {
                    stickyPrice.text(subTotal);
                }

                const thisStickyPrice = $('[data-sticky-add-to-cart] .sticky-price');
                const thisComparePrice = $('[data-sticky-add-to-cart] .money-compare-price');
                const compare_at_price = this.currentVariant?.compare_at_price;
                const current_at_price = this.currentVariant?.price;

                if(compare_at_price == current_at_price) {
                    thisStickyPrice.removeClass('has-compare-price');
                    thisComparePrice.remove();
                } else {
                    if (compare_at_price) {
                        thisStickyPrice.addClass('has-compare-price');
                        if (thisComparePrice.length) {
                            thisComparePrice.attr('data-compare-price', compare_at_price);
                        } else {
                            thisStickyPrice.prepend(`<s class="money-compare-price" data-compare-price="${compare_at_price}"><span class="money"></span></s>`);
                        }
                    } else {
                        thisStickyPrice.removeClass('has-compare-price');
                        thisComparePrice.remove();
                    }
                }

                if (subTotal != 0 && stickyComparePrice.length && window.subtotal.show) {
                    let comparePrice = $('[data-sticky-add-to-cart] .money-compare-price').data('compare-price'),
                        qty = quantityInput.val() || 1;
                    comparePrice = qty * comparePrice;
                    comparePrice = Shopify.formatMoney(comparePrice, window.money_format);
                    comparePrice = extractContent(comparePrice);
                    stickyComparePrice.text(comparePrice);
                }

                if (notifyMe.length > 0) {
                    notifyMe.find('.halo-notify-product-variant').val(this.currentVariant.title);
                    notifyMe.find('.notifyMe-text').empty();
                    notifyMe.slideDown('slow');
                }
            } else {
                var text,
                    subTotal = 0,
                    price = this.currentVariant?.price;

                const stickyPrice = $('[data-sticky-add-to-cart] .money-subtotal .money');

                if (window.subtotal.show) {
                    let qty = quantityInput.val() || 1;

                    subTotal = qty * price;
                    subTotal = Shopify.formatMoney(subTotal, window.money_format);
                    subTotal = extractContent(subTotal);

                    const moneySpan = document.createElement('span')
                    moneySpan.classList.add(window.currencyFormatted ? 'money' : 'money-subtotal')
                    moneySpan.innerText = subTotal
                    document.body.appendChild(moneySpan)

                    if (this.checkNeedToConvertCurrency()) {
                        let currencyCode = document.getElementById('currencies')?.querySelector('.active')?.getAttribute('data-currency');
                        Currency.convertAll(window.shop_currency, currencyCode, 'span.money', 'money_format');
                    }

                    subTotal = moneySpan.innerText
                    $(moneySpan).remove()

                    if (window.subtotal.style == '1') {
                        const pdView_subTotal = document.querySelector('.productView-subtotal .money') || document.querySelector('.productView-subtotal .money-subtotal');
                        if (pdView_subTotal != null) {
                            pdView_subTotal.textContent = subTotal;
                        }

                        if (this.currentVariant.available && maxValue <= 0 && this.currentVariant.inventory_management == "shopify") {
                            text = window.variantStrings.preOrder;
                        } else {
                            text = window.variantStrings.addToCart;
                        }
                    } else if (window.subtotal.style == '2') {
                        if (this.currentVariant.available && maxValue <= 0 && this.currentVariant.inventory_management == "shopify") {
                            text = window.variantStrings.preOrder;
                        } else {
                            text = window.subtotal.text.replace('[value]', subTotal);
                            $('#show-sticky-product').text(text);
                            $('#product-sticky-add-to-cart').text(text);
                            console.log(text,'345');
                        }
                    }
                } else {
                    subTotal = Shopify.formatMoney(price, window.money_format);
                    subTotal = extractContent(subTotal);
                    if (this.currentVariant.available && maxValue <= 0 && this.currentVariant.inventory_management == "shopify") {
                        text = window.variantStrings.preOrder;
                    } else {
                        text = window.variantStrings.addToCart;
                    }
                }

                quantityInput.attr('data-price', this.currentVariant?.price);
                quantityInput.attr('disabled', false);
                addButton.removeAttribute('disabled');
                addButton.textContent = text;
                quantityInput.closest('quantity-input').removeClass('disabled');

                if (subTotal != 0 && stickyPrice.length) {
                    stickyPrice.text(subTotal);
                }

                const thisStickyPrice = $('[data-sticky-add-to-cart] .sticky-price');
                const thisComparePrice = $('[data-sticky-add-to-cart] .money-compare-price');
                const compare_at_price = this.currentVariant?.compare_at_price;
                const current_at_price = this.currentVariant?.price;

                if(compare_at_price == current_at_price) {
                    thisStickyPrice.removeClass('has-compare-price');
                    thisComparePrice.remove();
                } else {
                    if (compare_at_price) {
                        thisStickyPrice.addClass('has-compare-price');
                        if (thisComparePrice.length) {
                            thisComparePrice.attr('data-compare-price', compare_at_price);
                        } else {
                            thisStickyPrice.prepend(`<s class="money-compare-price" data-compare-price="${compare_at_price}"><span class="money"></span></s>`);
                        }
                    } else {
                        thisStickyPrice.removeClass('has-compare-price');
                        thisComparePrice.remove();
                    }
                }

                const stickyComparePrice = $('[data-sticky-add-to-cart] .money-compare-price .money');
                if (subTotal != 0 && stickyComparePrice.length && window.subtotal.show) {
                    let comparePrice = $('[data-sticky-add-to-cart] .money-compare-price').data('compare-price'),
                        qty = quantityInput.val() || 1;
                    comparePrice = qty * comparePrice;
                    comparePrice = Shopify.formatMoney(comparePrice, window.money_format);
                    comparePrice = extractContent(comparePrice);
                    stickyComparePrice.text(comparePrice);
                }

                if (notifyMe.length > 0) {
                    notifyMe.slideUp('slow');
                }
            }
        }
    }

    updateStickyAddToCart(unavailable = true, disable = true){
        if(this.item.find('.productView-stickyCart').length > 0){
            const sticky = this.item.find('.productView-stickyCart');
            const itemImage = sticky.find('.sticky-image');
            const option = sticky.find('.select__select');
            const input = document.getElementById(`product-form-sticky-${this.dataset.product}`)?.querySelector('input[name="id"]');
            const button = document.getElementById(`product-form-sticky-${this.dataset.product}`)?.querySelector('[name="add"]');
            if (!button || !input) return;
            var quantityInput = this.item.find('input[name="quantity"]');
            var submitBtn = $(".product-form__submit");
            var maxValue;

            if (quantityInput.length > 0) {
                maxValue = parseInt(quantityInput.attr('data-inventory-quantity'));
            } else {
                maxValue = parseInt(submitBtn.attr('data-inventory-quantity'));
            }
          
            if(unavailable){
                var text = window.variantStrings.unavailable;

                button.setAttribute('disabled', true);
                button.textContent = text;
            } else {
                if (!this.currentVariant) return;

                const image = this.currentVariant?.featured_image;
                
                if (image != null) {
                    const imageSource = Shopify.productImageUrl(image.src, image.width);
                    itemImage.find('img').attr({
                        'src': imageSource,
                        'srcset': imageSource,
                        'alt': image.alt
                    });
                }

                option.val(this.currentVariant.id);

                if (disable) {
                    var text = this.getSoldOutGuidance()?.button || window.variantStrings.soldOut;

                    button.setAttribute('disabled', true);
                    button.textContent = text;
                } else {
                    if (this.currentVariant.available && maxValue <= 0 && this.currentVariant.inventory_management == "shopify") {
                        text = window.variantStrings.preOrder;
                    } else {
                        text = window.variantStrings.addToCart;
                    }

                    button.removeAttribute('disabled');
                    button.textContent = text;
                }

                input.value = this.currentVariant.id;
                input.dispatchEvent(new Event('change', { bubbles: true }));
            }
        }
    }

    getVariantData() {
        this.variantData = this.variantData || JSON.parse(this.querySelector('[type="application/json"]').textContent);
        return this.variantData;
    }

    getSoldOutGuidance() {
        if (!this.currentVariant || this.currentVariant.available) return null;
        const availableVariants = this.getVariantData().filter((variant) => variant.available);
        if (!availableVariants.length) return null;

        const sizeField = Array.from(this.querySelectorAll('[data-option-index]')).find((field) =>
            field.querySelector('input[name="Tamanho"], select[name="options[Tamanho]"]'));
        const sizeIndex = Number(sizeField?.dataset.optionIndex);
        const anotherSizeForSameOptions = sizeField && availableVariants.some((variant) =>
            variant.options.every((value, index) =>
                index === sizeIndex || value === this.currentVariant.options[index]));

        return anotherSizeForSameOptions
            ? { button: window.variantStrings.selectAnotherSize, message: window.variantStrings.selectedSizeUnavailable }
            : { button: window.variantStrings.selectAnotherOption, message: window.variantStrings.selectedOptionUnavailable };
    }

    checkNeedToConvertCurrency() {
        var currencyItem = $('.dropdown-item[data-currency]');
        if (currencyItem.length) {
            return (window.show_multiple_currencies && Currency.currentCurrency != shopCurrency) || window.show_auto_currency;
        } else {
            return;
        }
    }

    checkQuantityWhenVariantChange() {
        if (!this.currentVariant) return;
        var quantityInput = this.closest('.productView-details')?.querySelector('input.quantity__input')
        var maxValue = parseInt(quantityInput?.dataset.inventoryQuantity);
        var inputValue = parseInt(quantityInput?.value);

        let value = inputValue 

        if (inputValue > maxValue && maxValue > 0) {
            value = maxValue
        } else {
            value = inputValue
        }

        if (value < 1 || isNaN(value)) value = 1 
      
        if (quantityInput) {
          quantityInput.value = value
        }
      
        const button = this.item[0]?.querySelector('#product-add-to-cart');
        if (button) button.dataset.available = this.currentVariant.available && maxValue <= 0
    }

    updateVariantStatuses() {
        const variants = this.getVariantData();
        const wrappers = Array.from(this.querySelectorAll('.product-form__input'));
        wrappers.forEach((wrapper, index) => {
            const header = wrapper.querySelector('[data-header-option]');
            if (header) header.textContent = this.options[index] || '';
            const candidates = variants.filter((variant) => this.options.slice(0, index).every((value, previous) => value === variant.options[previous]));
            this.setInputAvailability(Array.from(wrapper.querySelectorAll('input[type="radio"], option')),
                candidates.map((variant) => variant.options[index]),
                candidates.filter((variant) => variant.available).map((variant) => variant.options[index]));
        });
        if (this.querySelectorAll('.product-form__swatch').length > 1) {
            this.updateImageSwatch(variants.filter((variant) => variant.option1 === this.options[0]));
        }
        this.syncLinkedPickers();
    }

    updateImageSwatch(selectedOptionOneVariants) {
        const inputWrappers = this.querySelectorAll('.product-form__input');
        if(inputWrappers){
            inputWrappers.forEach((element, inputIndex) => {
                const imageSpan = element.querySelectorAll("label>span.pattern");
                const imageLabel = element.querySelectorAll("label");
                const imageSpanImage = element.querySelectorAll("label>span.expand>img");
                const inputList = element.querySelectorAll("input");

                inputList.forEach((item, index) => {
                    const image = selectedOptionOneVariants.filter(tmp => {
                        if (inputIndex == 0) return tmp.option1 == item.value;
                        if (inputIndex == 1) return tmp.option2 == item.value;
                        if (inputIndex == 2) return tmp.option3 == item.value;
                    });
    
                    if(image.length > 0) {
                        imageLabel[index].style.display = "inline-block";
                        if (imageSpan[index] != undefined && image[0].featured_image != null) imageSpan[index].style.backgroundImage = `url("${Shopify.productImageUrl(image[0].featured_image.src, image[0].featured_image.width)}")`;
                        if (imageSpanImage[index] != undefined && image[0].featured_image != null) imageSpanImage[index].srcset = Shopify.productImageUrl(image[0].featured_image.src, image[0].featured_image.width);
                    }
                    // else {
                    //     imageLabel[index].style.display = "none";
                    // }
                })
            });
        }
    }            

    setInputAvailability(optionInputs, optionInputsValue, availableOptionInputsValue) {
        optionInputs.forEach(input => {
            if (availableOptionInputsValue.includes(input.getAttribute('value'))) {
                input.classList.remove('soldout');
                input.innerText = input.getAttribute('value');
            } else {
                input.classList.add('soldout');
                optionInputsValue.includes(input.getAttribute('value')) ? input.innerText = input.getAttribute('value') + ' (Sold out)' : input.innerText = window.variantStrings.unavailable_with_option.replace('[value]', input.getAttribute('value'))
            }
        });
    }

    updateBadgeSale() {
        let badgeWrap = document.querySelector(".productView-badge");
        if (!badgeWrap) return;
        let
            badgeText = badgeWrap.querySelector(".productView-badge .badge.sale-badge"),
            badgeTextSaleType = badgeWrap.getAttribute('data-text-sale-badge'),
            priceCompare = this.item[0]?.querySelector(".productView-product .price__compare")?.getAttribute('data-compare'),
            priceLast = this.item[0]?.querySelector(".productView-product .price__sale .price__last")?.getAttribute('data-last'),
            percentSale = Math.round(((priceCompare - priceLast) / priceCompare) * 100);

        function renderSpan(text1, text2) {
            let spanElement = document.createElement('span');
            spanElement.classList.add('badge', 'sale-badge');
            let container = document.querySelector('.productView-badge');
            container.appendChild(spanElement);
            spanElement.innerText = `${text1} ${text2}%OFF`
        }

        if (badgeWrap.classList.contains("has-badge-js")) {
            if (!priceCompare || percentSale <= 0) {
                badgeWrap.querySelector(".badge.sale-badge")?.remove();
            } else if (percentSale > 0) {
                if (!badgeText) {
                    renderSpan('', percentSale)
                } else {
                    badgeText.innerText = `${percentSale}%OFF`
                }
            }
        } else {
            if (!priceCompare || percentSale <= 0) {
                badgeWrap.querySelector(".badge.sale-badge")?.remove();
            } else if (percentSale > 0) {
                if (!badgeText) {
                    renderSpan(badgeTextSaleType, percentSale);
                } else {
                    badgeText.innerText = `${badgeTextSaleType}`
                }
            }
        }

    }
}

customElements.define('variant-selects', VariantSelects);

class VariantRadios extends VariantSelects {
    constructor() {
        super();
    }

    setInputAvailability(optionInputs, optionInputsValue, availableOptionInputsValue) {
        optionInputs.forEach((input) => {
            const label = input.labels?.[0] || input.nextElementSibling;
            if (!label) return;
            const value = this.decodeOption(input.value);
            const available = availableOptionInputsValue.includes(value);
            label.classList.toggle('available', available);
            label.classList.toggle('soldout', !available);
            label.classList.toggle('unavailable', !input.checked && window.variantStrings.hide_variants_unavailable && !optionInputsValue.includes(value));
        });
    }

    updateOptions() {
        const fieldsets = Array.from(this.querySelectorAll('fieldset'));
        this.options = fieldsets.map((fieldset) => {
            return fieldset.querySelector('input:checked')?.value || null;
        });
    }
}

customElements.define('variant-radios', VariantRadios);

class QuantityInput extends HTMLElement {
    constructor() {
        super();
        this.input = this.querySelector('input');
        this.changeEvent = new Event('change', { bubbles: true });
        this.input.addEventListener('change', this.onInputChange.bind(this));

        this.querySelectorAll('button').forEach(
            (button) => button.addEventListener('click', this.onButtonClick.bind(this))
        );

        if (!this.checkHasMultipleVariants()) {
            this.initProductQuantity();
            this.checkVariantInventory();
        }
    }

    onInputChange(event) {
        event.preventDefault(); 
        var inputValue = this.input.value;
        var maxValue = parseInt(this.input.dataset.inventoryQuantity);
        var currentId = document.getElementById(`product-form-${this.input.dataset.product}`)?.querySelector('[name="id"]')?.value;
        var saleOutStock  = document.getElementById('product-add-to-cart').dataset.available === 'true' || false ;
        const addButton = document.getElementById(`product-form-${this.input.dataset.product}`)?.querySelector('[name="add"]');

        if(inputValue < 1) {
            inputValue = 1;

            this.input.value =  inputValue;
        }
        
        if (inputValue > maxValue && !saleOutStock && maxValue > 0) {
            var arrayInVarName = `selling_array_${this.input.dataset.product}`,
                itemInArray = window[arrayInVarName],
                itemStatus = itemInArray[currentId];
          
            if(itemStatus == 'deny') {
              inputValue = maxValue
              this.input.value = inputValue;
              const message = getInputMessage(maxValue)
              showWarning(message, 3000)
            }
        } else if (inputValue > maxValue && saleOutStock && maxValue <= 0) {
            this.input.value = inputValue;
        }

        if(window.subtotal.show) {
            var text,
                price = this.input.dataset.price,
                subTotal = 0;

            var parser = new DOMParser();

            subTotal = inputValue * price;
            subTotal = Shopify.formatMoney(subTotal, window.money_format);
            subTotal = extractContent(subTotal);

            const moneySpan = document.createElement('span')
            moneySpan.classList.add(window.currencyFormatted ? 'money' : 'money-subtotal') 
            moneySpan.innerText = subTotal 
            document.body.appendChild(moneySpan) 

            if (this.checkNeedToConvertCurrency()) {
                let currencyCode = document.getElementById('currencies')?.querySelector('.active')?.getAttribute('data-currency');
                Currency.convertAll(window.shop_currency, currencyCode, 'span.money', 'money_format');
            }

            subTotal = moneySpan.innerText 
            $(moneySpan).remove()

            if (window.subtotal.style == '1') {
                const pdView_subTotal = document.querySelector('.productView-subtotal .money') || document.querySelector('.productView-subtotal .money-subtotal');

                pdView_subTotal.textContent = subTotal;
            }
            else if (window.subtotal.style == '2') {
                text = window.subtotal.text.replace('[value]', subTotal);
                $('#product-sticky-add-to-cart').text(text);

                addButton.textContent = text;  
            }

            const stickyPrice = $('[data-sticky-add-to-cart] .money-subtotal .money');
            const stickyComparePrice = $('[data-sticky-add-to-cart] .money-compare-price .money');

            if (stickyPrice.length) {
                stickyPrice.text(subTotal);
            }

            if (stickyComparePrice.length && window.subtotal.show) {
                let comparePrice = $('[data-sticky-add-to-cart] .money-compare-price').data('compare-price');
                comparePrice = inputValue * comparePrice;
                comparePrice = Shopify.formatMoney(comparePrice, window.money_format);
                comparePrice = extractContent(comparePrice);
                stickyComparePrice.text(comparePrice);
            }
        }

        if (this.classList.contains('quantity__group--2') || this.classList.contains('quantity__group--3')) {
            const mainQty = document.querySelector('.quantity__group--1 .quantity__input');
            mainQty.value = inputValue;

            const mainQty2Exists = !!document.querySelector('.quantity__group--2 .quantity__input');
            const mainQty3Exists = !!document.querySelector('.quantity__group--3 .quantity__input');

            if (this.classList.contains('quantity__group--2') && mainQty3Exists) {
                const mainQty3 = document.querySelector('.quantity__group--3 .quantity__input');
                mainQty3.value = inputValue;
            }
            else if (this.classList.contains('quantity__group--3') && mainQty2Exists) {
                const mainQty2 = document.querySelector('.quantity__group--2 .quantity__input');
                mainQty2.value = inputValue;
            }
        }
    }

    onButtonClick(event) {
        event.preventDefault();
        const previousValue = this.input.value;
        
        event.target.name === 'plus' ? this.input.stepUp() : this.input.stepDown();
        if (previousValue !== this.input.value) this.input.dispatchEvent(this.changeEvent);
    }

    checkNeedToConvertCurrency() {
        return (window.show_multiple_currencies && Currency.currentCurrency != shopCurrency) || window.show_auto_currency;
    }
    
    checkHasMultipleVariants() {
        const arrayInVarName = `product_inven_array_${this.querySelector('[data-product]').dataset.product}`
        this.inven_array = window[arrayInVarName];
        return Object.keys(this.inven_array).length > 1
    }
        
    initProductQuantity() {
        if(this.inven_array != undefined) {
            var inven_num = Object.values(this.inven_array),
                inventoryQuantity = parseInt(inven_num);

            this.querySelector('input[name="quantity"]').setAttribute('data-inventory-quantity', inventoryQuantity);
            this.querySelector('input[name="quantity"]').dataset.inventoryQuantity = inventoryQuantity
        }
    }

    checkVariantInventory() {
        const addBtn = document.getElementById('product-add-to-cart')
        this.input.disabled = addBtn.disabled 
        this.querySelector('.btn-quantity.minus').disabled = addBtn.disabled
        this.querySelector('.btn-quantity.plus').disabled = addBtn.disabled
    }

    getVariantData() {
        this.variantData = this.variantData || JSON.parse(document.querySelector('.product-option [type="application/json"]').textContent);
        return this.variantData;
    }
}

customElements.define('quantity-input', QuantityInput);

function hotStock(inventoryQuantity) {
    const productView = document.querySelector('.productView');
    const hotStock = productView.querySelector('.productView-hotStock');
    if(hotStock) {
        let hotStockText = hotStock.querySelector('.hotStock-text'),
            maxStock = parseFloat(hotStock.dataset.hotStock),
            textStock;

        if(inventoryQuantity > 0 && inventoryQuantity <= maxStock){
            hotStock.matches('.style-2') ? textStock  = window.inventory_text.hotStock2.replace('[inventory]', inventoryQuantity) : textStock  = window.inventory_text.hotStock.replace('[inventory]', inventoryQuantity);
            if (hotStockText) hotStockText.innerHTML = textStock;
            hotStock.classList.remove('is-hide');
        } else {
            hotStock.classList.add('is-hide');
        }

        if (hotStock.matches('.style-2')) {
            const invenProgress = inventoryQuantity / maxStock * 100,
                hotStockProgressItem = hotStock.querySelector('.hotStock-progress-item');
            if (hotStockProgressItem) hotStockProgressItem.style.width = `${invenProgress}%`;
        }
    }
}
const hotStockNoOptions = document.querySelector('.productView .productView-hotStock[data-current-inventory]');
if (hotStockNoOptions) {
    const inventoryQuantity = parseFloat(hotStockNoOptions.dataset.currentInventory);
    hotStock(inventoryQuantity);
}


function showWarning(content, time = null) {
    if (window.warningTimeout) {
        clearTimeout(window.warningTimeout);
    }
    const warningPopupContent = document.getElementById('halo-warning-popup').querySelector('[data-halo-warning-content]')
    warningPopupContent.textContent = content
    document.body.classList.add('has-warning')

    if (time) {
        window.warningTimeout = setTimeout(() => {
            document.body.classList.remove('has-warning')
        }, time)
    }
}

function getInputMessage(maxValue) {
    var message = window.cartStrings.addProductOutQuantity.replace('[maxQuantity]', maxValue);
    return message
}
